import React, { useId, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { useCart } from '../../contexts/CartContext';
import { buildTokens, tokensToCssVars } from '../customer/mealpass/storefrontTheme';
import { formatPrice } from '../../utils/currency';
import { dateLabel, cutoffLabel } from './MealPackBuilder';

export function composedQuote(config, picks, count = 1) {
  const doubled = config.categories.some(c => c.allow_double && c.choices.some(o => picks[o.item_id] === 2));
  const premiums = config.categories.flatMap(c => c.choices).reduce((sum, o) => sum + (picks[o.item_id] ? Math.round((o.surcharge || 0) * 100) : 0), 0);
  return ((Math.round((doubled ? config.double_price : config.base_price) * 100) + premiums) * count) / 100;
}

export function completeComponentChoices(config, picks, available) {
  if (!config?.categories?.length) return false;
  const allowed = new Set(available.map(i => i.item_id));
  const configured = new Set(config.categories.flatMap(c => c.choices.map(o => o.item_id)));
  return Object.keys(picks).every(id => configured.has(id) && allowed.has(id)) && config.categories.every(c => {
    const selected = c.choices.filter(o => picks[o.item_id]);
    return selected.length >= Number(c.required) && selected.length <= c.max_choices &&
      selected.every(o => allowed.has(o.item_id) && (picks[o.item_id] === 1 || (c.allow_double && picks[o.item_id] === 2)));
  });
}

export default function ComposedMealBuilder({ mealPlan, options, storeId, storeSlug, onClose, onCheckout, brand, brandingHint }) {
  const { addToCart } = useCart();
  const [day, setDay] = useState('');
  const [picks, setPicks] = useState({});
  const [addedMeals, setAddedMeals] = useState([]);
  const addedCount = addedMeals.length;
  const count = 1;
  const config = options.selling.composed_meal;
  const money = value => formatPrice(value, options.currency);
  const available = options.items.filter(i => (options.items_by_date[day] || []).includes(i.item_id));
  const selectedDate = options.dates.find(d => d.date === day);
  const complete = selectedDate && completeComponentChoices(config, picks, available);
  const total = composedQuote(config, picks, count);
  const add = () => {
    if (!complete) return;
    const selections = Object.entries(picks).map(([item_id, quantity]) => ({ item_id, quantity }));
    const weekday = (new Date(`${day}T12:00:00`).getDay() + 6) % 7;
    addToCart({ ...mealPlan, currency: options.currency }, {
      quantity: count, packSize: count, packPricingMode: 'category_meal', packSelections: selections,
      menuRevision: options.menus_by_date[day].revision, purchaseType: 'one_time',
      deliveryDays: Object.fromEntries(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map((d, i) => [d, Number(i === weekday)])),
      price: total, startDate: day, storeId, storeSlug, cutoffAt: selectedDate.cutoff_at, timezone: options.timezone,
      selectedMealNames: selections.map(p => ({ ...p, quantity: count,
        name: `${available.find(i => i.item_id === p.item_id)?.name}${p.quantity === 2 ? ' (Double portion)' : ''}` })),
    });
    setAddedMeals(rows => [...rows, {total, day, components: selections.map(p => `${available.find(i => i.item_id === p.item_id)?.name}${p.quantity === 2 ? ' (Double portion)' : ''}`).join(', ')}]);
    setPicks({});
  };
  return <Dialog open onOpenChange={open => { if (!open) onClose(); }}>
    <DialogContent style={tokensToCssVars(buildTokens(brand || brandingHint || { primary_color: '#1d4b3a' }))} className="meal-pack-surface mp-builder !flex !flex-col !gap-0 !p-0 !max-w-4xl !w-[calc(100vw-24px)] !max-h-[92dvh]">
      <header className="border-b px-5 py-4 pr-10">
        <DialogTitle>{mealPlan.alias || mealPlan.name}</DialogTitle>
        <DialogDescription>Build one meal from your favorite components. One-time purchase.</DialogDescription>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto p-5 space-y-6">
        <label className="block font-semibold">Delivery date
          <select className="mt-2 w-full rounded-lg border p-3" value={day} onChange={e => { setDay(e.target.value); setPicks({}); }}>
            <option value="">Choose a delivery date</option>
            {options.dates.map(d => <option key={d.date} value={d.date}>{dateLabel(d.date)}</option>)}
          </select>
        </label>
        {!options.dates.length && <p role="status">No delivery dates are currently available.</p>}
        {selectedDate && <p className="text-sm">Order before {cutoffLabel(selectedDate.cutoff_at, options.timezone)}.</p>}
        {day && <ComponentChoices config={config} picks={picks} setPicks={setPicks} available={available} money={money} />}
        {addedCount > 0 && <p role="status">{addedCount} meal{addedCount === 1 ? '' : 's'} added to your cart. Build another with the same or different choices.</p>}
        {addedMeals.map((meal, index) => <div key={index} className="flex items-start justify-between gap-3 rounded-lg border p-3">
          <div><strong>Meal {index + 1}</strong><p className="text-sm">{meal.components}</p><p className="text-sm">{dateLabel(meal.day)}</p></div>
          <strong className="whitespace-nowrap">{money(meal.total)}</strong>
        </div>)}
        <p className="text-sm text-slate-600">Each click on Add meal adds one configured meal to your cart.
          {options.minimum_order_value > 0 && <> Minimum {money(options.minimum_order_value)} per delivery, before fees, tax and tip.</>}
        </p>
      </div>
      <footer className="flex flex-wrap items-center justify-between gap-3 border-t p-5">
        <div><strong className="text-xl">{money(total)}</strong><small className="block">{count} meal{count === 1 ? '' : 's'} · fees and tax at checkout</small></div>
        <button type="button" className="mp-primary rounded-lg px-5 py-3 disabled:opacity-40" disabled={!complete} onClick={add}>Add meal</button>
        {addedCount > 0 && <button type="button" className="rounded-lg border px-5 py-3" onClick={() => {onClose(); onCheckout?.();}}>Continue to checkout</button>}
      </footer>
    </DialogContent>
  </Dialog>;
}

export function ComponentChoices({ config, picks, setPicks, available, money }) {
  const groupId = useId();
  const select = (category, id) => setPicks(old => {
    const next = { ...old };
    if (next[id]) { delete next[id]; return next; }
    if (category.max_choices === 1) category.choices.forEach(o => delete next[o.item_id]);
    else if (category.choices.filter(o => next[o.item_id]).length >= category.max_choices) return old;
    next[id] = 1;
    return next;
  });
  return <div className="space-y-5">        {config.categories.map(category => <fieldset key={category.name} className="rounded-xl border p-4">
          <legend className="px-2 font-semibold">{category.name}</legend>
          <p className="mb-3 text-sm text-slate-600">{category.required ? 'Required' : 'Optional'} · Choose {category.max_choices === 1 ? 'one' : `up to ${category.max_choices}`}</p>
          <div className="grid gap-2 sm:grid-cols-2">{category.choices.map(choice => {
            const item = available.find(i => i.item_id === choice.item_id);
            if (!item) return null;
            const atLimit = category.max_choices > 1 && !picks[choice.item_id] && category.choices.filter(o => picks[o.item_id]).length >= category.max_choices;
            return <label key={choice.item_id} style={picks[choice.item_id] ? { borderColor: 'var(--sf-primary, #0f766e)' } : undefined} className={`flex items-center gap-3 rounded-lg border p-3 ${picks[choice.item_id] ? 'border-teal-700 bg-teal-50' : ''}`}>
              <input type={category.max_choices === 1 && category.required ? 'radio' : 'checkbox'} name={`${groupId}-component-${category.name}`}
                checked={!!picks[choice.item_id]} disabled={atLimit}
                onChange={() => select(category, choice.item_id)} />
              <span>{item.name}<small className="block text-slate-600">{choice.surcharge > 0 ? `+${money(choice.surcharge)}` : 'Included'}</small></span>
            </label>;
          })}</div>
          {category.allow_double && category.choices.some(o => picks[o.item_id]) && <label className="mt-4 flex items-start gap-2">
            <input type="checkbox" checked={category.choices.some(o => picks[o.item_id] === 2)}
              onChange={e => { const quantity = e.target.checked ? 2 : 1; setPicks(old => Object.fromEntries(Object.entries(old).map(([id, q]) => [id, category.choices.some(o => o.item_id === id) ? quantity : q]))); }} />
            <span>Double portion · meal base {money(config.double_price)}<small className="block text-slate-600">Selected premiums still apply.</small></span>
          </label>}
        </fieldset>)}
</div>;
}
